import type { SimpleProject } from '../../../e2e/e2e/common/schemas';
import type { RichLead } from '../../lead.popup/types';
import type { BaseUser, Deal, Unit, User } from '../../types';
import type { Appeal } from './appealsApi';
import type { AppealSource } from './appealSourceApi';
import type { Booking } from './bookingApi';
import type { ApiResponse } from './estClient';
import type { Interest } from './interestApi';
import type { RichUnit } from './unitApi';

import { cache } from '../cache';
import { estClient } from './estClient';
import { fixSelectedUnits as fixFavoriteUnits } from './normalizationFns';

export interface RichOpportunity {
  id: number;
  pipeline_id: number;
  lead_id: number;
  status: number;
  stage_id: number;
  max_stage_reached: number | null;
  label_id: number;
  manager_id: number;
  appeal_id: number | null;

  lead: RichLead;
  appeal: Appeal | Appeal[];
  bookings: Booking[];
  interests: Interest[];
  deals: Deal[];

  manager: BaseUser;
  created_by_user: BaseUser;
  updated_by_user: BaseUser;
  closed_by_user: BaseUser | null;

  created_at: string;
  created_by: number;

  updated_at: string;
  updated_by: number;

  closed_at: string | null;
  closed_by: number | null;
}

export interface OpportunityGetResponse {
  success: boolean;
  opportunities: RichOpportunity[];
}

interface OpportunityBaseResponse {
  success: boolean;
  opportunity: RichOpportunity;
}

export interface OpportunityLabel {
  id: number;
  name: string;
  stage_id: number;
  order: number;
  is_automated: boolean;
  is_system: boolean;
  is_stage_label: boolean;
  is_selectable: boolean;
  is_archived: boolean;
  can_delete: boolean;
}

export interface CreateOpportunityData {
  lead_id: RichLead['id'];
  object_id: SimpleProject['id'];
  ad_source_id: AppealSource['id'];
  custom_note?: string;
}

export interface UpdateOpportunityData {
  label_id?: number;
  pipeline_id?: number;
}

export interface CloseOpportunityData {
  force?: boolean;
  comment?: string;
}

class OpportunityApi {
  get ACTION() {
    return {
      VISIT: 1,
      UNIT_VIEW: 10,
      SELECTED_UNITS_VIEW: 20,
      SELECTED_UNIT_VIEW: 25,
      SELECTED_UNIT_ADD: 30,
      SELECTED_UNIT_REMOVE: 40,
      FILTERS_CHANGE: 60,
      SECTION_CHANGE: 70,
    } as const;
  }

  get ORIGIN() {
    return {
      MANAGER: 1,
      CLIENT: 2,
    } as const;
  }
  async getById(opportunityId: number): Promise<OpportunityGetResponse | null> {
    if (!opportunityId) {
      return null;
    }

    const resp = await estClient.get<OpportunityGetResponse>(
      `/api/opportunity/${opportunityId}`,
    );

    return resp;
  }

  async create(data: CreateOpportunityData): Promise<{
    appeal: Appeal;
    success: boolean;
  }> {
    const resp = await estClient.post<{
      appeal: Appeal;
      success: boolean;
    }>('/api/appeal', {
      body: {
        ...data,
        // pipeline_id: 0,
      },
    });

    return resp;
  }

  async update(
    opportunityId: number,
    data: UpdateOpportunityData,
  ): Promise<OpportunityBaseResponse> {
    const { opportunity, success } =
      await estClient.put<OpportunityBaseResponse>(
        `/api/opportunity/${opportunityId}`,
        { body: data },
      );

    if (opportunity.id) {
      return { opportunity, success };
    }

    throw new Error('Invalid response format');
  }

  async close(
    opportunityId: number,
    data?: CloseOpportunityData,
  ): Promise<OpportunityBaseResponse> {
    const { opportunity, success } =
      await estClient.post<OpportunityBaseResponse>(
        `/api/opportunity/${opportunityId}/close`,
        {
          body: {
            force: data?.force || false,
            comment: data?.comment,
          },
        },
      );

    if (opportunity.id) {
      return { opportunity, success };
    }

    throw new Error('Invalid response format');
  }

  async qualify(
    opportunityId: number,
    data: {
      lead_status_id: number;
      ad_source_id: number;
      appeals_close: number[];
      object_id?: number;
      appeal_comment?: string;
      units?: Record<
        string,
        {
          isInterested?: boolean;
          isBooked?: boolean;
          days?: number;
          unitStatusId?: string;
        }
      >;
    },
  ): Promise<{ success: boolean }> {
    const resp = await estClient.post<{ success: boolean }>(
      `/fe/opportunity/${opportunityId}/qualify`,
      {
        body: data,
      },
    );

    return resp;
  }

  async closeAppeals(
    opportunityId: number,
    data: {
      lead_status_id: number;
      ad_source_id: number;
      appeals_close: number[];
      object_id?: number;
      appeal_comment?: string;
      units?: Record<
        string,
        {
          isInterested?: boolean;
          isBooked?: boolean;
          days?: number;
          unitStatusId?: string;
        }
      >;
    },
  ): Promise<{ success: boolean }> {
    const resp = await estClient.post<{ success: boolean }>(
      `/fe/opportunity/${opportunityId}/close_appeals`,
      {
        body: data,
      },
    );

    return resp;
  }

  async disqualify(
    opportunityId: number,
    data: {
      lead_status_id: number;
      opportunity_label_id: number;
      appeal_comment?: string;
    },
  ): Promise<{ success: boolean }> {
    const resp = await estClient.post<{ success: boolean }>(
      `/fe/opportunity/${opportunityId}/disqualify`,
      {
        body: data,
      },
    );

    return resp;
  }

  async labelChange(
    opportunityId: number,
    data: {
      pipeline_id: number;
      label_id: number;
    },
  ): Promise<{ success: boolean }> {
    const resp = await estClient.put<{ success: boolean }>(
      `/api/opportunity/${opportunityId}`,
      {
        body: data,
      },
    );

    return resp;
  }

  getOpportunityLabels = ({
    cache: useCache = true,
  }: { cache?: boolean } = {}): Promise<{
    opportunity_labels: Array<OpportunityLabel>;
  } | null> => {
    return cache.get(
      '/api/opportunity/labels',
      () =>
        estClient.get<{ opportunity_labels: Array<OpportunityLabel> }>(
          `/api/opportunity/labels`,
        ),
      { cache: useCache },
    );
  };

  async getLabels(): Promise<{
    opportunity_labels: OpportunityLabel[];
  }> {
    return estClient.get<{ opportunity_labels: OpportunityLabel[] }>(
      '/api/db/opportunity_label',
    );
  }

  async createLabel({
    name,
    stageId,
  }: {
    name: string;
    stageId: number;
  }): Promise<void> {
    await estClient.post('/api/db/opportunity_label', {
      body: { name, stage_id: stageId, l10n_key: '' },
    });
  }

  async updateLabel(id: number, { name }: { name: string }): Promise<void> {
    await estClient.put(`/api/db/opportunity_label/${id}`, {
      body: { name },
    });
  }

  async deleteLabel(id: number): Promise<void> {
    await estClient.delete(`/api/db/opportunity_label/${id}`);
  }

  async toggleArchive(id: number): Promise<void> {
    await estClient.post(`/api/db/opportunity_label/${id}/archive`, {});
  }

  async reassign(opportunityId: number, managerId: number): Promise<void> {
    await estClient.put(`/api/opportunity/${opportunityId}/reassign`, {
      body: { manager_id: managerId },
    });
  }

  async getSelectedSegmentLink(leadId: number | string): Promise<string> {
    const resp = await estClient.get<{ success: boolean; url: string }>(
      '/api/opportunity/selected-segment/link',
      {
        query: {
          lead_id: leadId,
        },
      },
    );

    return resp.url;
  }

  async getSelectedSegmentFilters(
    leadId: number | string,
  ): Promise<unknown[] | null> {
    const resp = await estClient.get<{
      success: boolean;
      filters: unknown[] | null;
    }>('/api/opportunity/selected-segment/filters', {
      query: {
        lead_id: leadId,
      },
    });

    return resp.filters ?? null;
  }

  async getSelectedSegmentFiltersForVisitor({
    opportunityId,
    urlToken,
  }: {
    opportunityId: number;
    urlToken: string;
  }): Promise<unknown[] | null> {
    const resp = await estClient.get<
      ApiResponse<{ filters: unknown[] | null }>
    >('/api/opportunity/selected-segment/visitor/filters', {
      query: {
        opportunity_id: opportunityId,
        url_token: urlToken,
      },
    });

    return resp.filters ?? null;
  }

  async getVisitorOwnFilters({
    opportunityId,
    urlToken,
  }: {
    opportunityId: number;
    urlToken: string;
  }): Promise<unknown[] | null> {
    const resp = await estClient.get<
      ApiResponse<{ filters: unknown[] | null }>
    >('/api/opportunity/selected-segment/visitor/own-filters', {
      query: {
        opportunity_id: opportunityId,
        url_token: urlToken,
      },
    });

    return resp.filters ?? null;
  }

  async saveVisitorFilters({
    opportunityId,
    urlToken,
    filters,
  }: {
    opportunityId: number;
    urlToken: string;
    filters: unknown[];
  }): Promise<ApiResponse> {
    return estClient.post<ApiResponse>(
      '/api/opportunity/selected-segment/visitor/filters',
      {
        body: {
          opportunity_id: opportunityId,
          url_token: urlToken,
          filters: JSON.stringify(filters),
        },
      },
    );
  }

  async getSelectedUnitSegments(
    leadId: number | string,
  ): Promise<{ house_id: number; section_ids: number[] }[] | null> {
    const resp = await estClient.get<
      ApiResponse<{
        selected_unit_segments:
          | { house_id: number; section_ids: number[] }[]
          | null;
      }>
    >('/api/opportunity/selected-segment/unit-segments', {
      query: {
        lead_id: leadId,
      },
    });

    return resp.selected_unit_segments ?? null;
  }

  async saveSelectedSegmentFilters({
    leadId,
    filters,
    selectedUnitSegments,
  }: {
    leadId: number | string;
    filters: unknown[];
    selectedUnitSegments?: unknown;
  }): Promise<ApiResponse> {
    const body: Record<string, unknown> = {
      lead_id: leadId,
      filters: JSON.stringify(filters),
    };

    if (selectedUnitSegments !== undefined) {
      body.selected_unit_segments = JSON.stringify(selectedUnitSegments);
    }

    return estClient.post<ApiResponse>(
      '/api/opportunity/selected-segment/filters',
      { body },
    );
  }

  async getFavoriteUnits(leadId: number | string): Promise<FavoriteUnitRef[]> {
    const resp = await estClient.get<ApiResponse<{ units: FavoriteUnitRef[] }>>(
      '/api/opportunity/selected-segment/units',
      {
        query: {
          lead_id: leadId,
        },
      },
    );

    for (const ref of resp.units) {
      fixFavoriteUnits(ref);
    }

    return resp.units;
  }

  async addFavoriteUnit({
    leadId,
    unitId,
  }: {
    leadId: number | string;
    unitId: number | string;
  }): Promise<void> {
    await estClient.post('/api/opportunity/selected-segment/unit', {
      body: {
        lead_id: leadId,
        unit_id: unitId,
      },
    });
  }

  async removeFavoriteUnit({
    leadId,
    unitId,
  }: {
    leadId: number | string;
    unitId: number | string;
  }): Promise<void> {
    await estClient.delete('/api/opportunity/selected-segment/unit', {
      body: {
        lead_id: leadId,
        unit_id: unitId,
      },
    });
  }

  async addFavoriteUnitForVisitor({
    opportunityId,
    urlToken,
    unitId,
  }: {
    opportunityId: number;
    urlToken: string;
    unitId: number | string;
  }): Promise<void> {
    await estClient.post('/api/opportunity/selected-segment/visitor/unit', {
      body: {
        opportunity_id: opportunityId,
        url_token: urlToken,
        unit_id: unitId,
      },
    });
  }

  async removeFavoriteUnitForVisitor({
    opportunityId,
    urlToken,
    unitId,
  }: {
    opportunityId: number;
    urlToken: string;
    unitId: number | string;
  }): Promise<void> {
    await estClient.delete('/api/opportunity/selected-segment/visitor/unit', {
      body: {
        opportunity_id: opportunityId,
        url_token: urlToken,
        unit_id: unitId,
      },
    });
  }

  async getLogs(opportunityId: number): Promise<
    ApiResponse<{
      logs: {
        id: number;
        action: number;
        action_label: string;
        origin: number;
        unit_id: number | null;
        section_id: number | null;
        created_at: string;
        created_by_name: string | null;
      }[];
    }>
  > {
    return estClient.get('/api/opportunity/selected-segment/logs', {
      query: {
        opportunity_id: opportunityId,
      },
    });
  }

  /**
   * Returns data for public (client facing) workspace page with proposed units and projects
   */
  async getWorkspaceUnits({
    opportunityId,
    urlToken,
  }: {
    opportunityId: number;
    urlToken: string;
  }): Promise<
    ApiResponse<{
      active_units: Unit[];
      deleted_units: Unit[];
      favorite_units: FavoriteUnitRef[];
      manager: Pick<User, 'username' | 'image'>;
    }>
  > {
    return estClient.get('/api/opportunity/selected-segment/workspace/units', {
      query: {
        opportunity_id: opportunityId,
        url_token: urlToken,
      },
    });
  }

  async logSelectedSegmentAction({
    opportunityId,
    urlToken,
    action,
    unitId,
    sectionId,
  }: {
    opportunityId: number;
    urlToken: string;
    action: number;
    unitId?: number;
    sectionId?: number;
  }): Promise<void> {
    await estClient.post('/api/opportunity/selected-segment/visitor/log', {
      body: {
        opportunity_id: opportunityId,
        url_token: urlToken,
        action,
        unit_id: unitId,
        section_id: sectionId,
      },
    });
  }

  get STATUS() {
    return {
      ACTIVE: 1,
      WON: 2,
      LOST: 3,
    } as const;
  }
}

export type FavoriteUnitRef = {
  id: RichUnit['unit_id'];
  origin: OpportunityApi['ORIGIN'][keyof OpportunityApi['ORIGIN']];
};

export const opportunityApi = new OpportunityApi();
